import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";

interface ScatterTextProps {
  text: string;
  fontSize: number;
}

const ScatterText: React.FC<ScatterTextProps> = ({ text, fontSize }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMouse = useRef({ x: 0, y: 0, time: Date.now() });
  const offsets = useRef<
    Record<number, { x: number; y: number; rotX: number; rotY: number }>
  >({});

  // Split text into characters and keep spaces
  const charArray: { char: string; isSpace: boolean }[] = [];
  text.split(" ").forEach((word, wordIdx, arr) => {
    word.split("").forEach((c) => charArray.push({ char: c, isSpace: false }));
    if (wordIdx < arr.length - 1) {
      charArray.push({ char: " ", isSpace: true });
    }
  });
  useEffect(() => {
    const interval = setInterval(() => {
      handleReset();
    }, 5000); // ทุก 5 วินาที

    return () => clearInterval(interval); // ล้าง interval ตอน unmount
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const chars = container.querySelectorAll<HTMLElement>(".char");
    if (!chars) return;

    chars.forEach((_, idx) => {
      offsets.current[idx] = { x: 0, y: 0, rotX: 0, rotY: 0 };
    });

    const onMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      const dt = now - prevMouse.current.time || 16;
      const dx = e.clientX - prevMouse.current.x;
      const dy = e.clientY - prevMouse.current.y;

      const vx = dx / dt;
      const vy = dy / dt;

      prevMouse.current = { x: e.clientX, y: e.clientY, time: now };

      chars.forEach((char, idx) => {
        const rect = char.getBoundingClientRect();
        if (
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom
        ) {
          const force = 150;
          offsets.current[idx].x += vx * force;
          offsets.current[idx].y += vy * force;
          offsets.current[idx].rotX += -vy * 5;
          offsets.current[idx].rotY += vx * 5;

          gsap.to(char, {
            x: offsets.current[idx].x,
            y: offsets.current[idx].y,
            rotationX: offsets.current[idx].rotX * 25,
            rotationY: offsets.current[idx].rotY * 25,
            rotationZ: offsets.current[idx].rotY * 25,
            duration: 2,
            ease: "power2.out",
          });
        }
      });
    };

    container.addEventListener("mousemove", onMouseMove);
    return () => container.removeEventListener("mousemove", onMouseMove);
  }, [text]);

  // Reset function
  const handleReset = () => {
    const chars = containerRef.current?.querySelectorAll<HTMLElement>(".char");
    if (!chars) return;

    chars.forEach((char, idx) => {
      offsets.current[idx] = { x: 0, y: 0, rotX: 0, rotY: 0 };
      gsap.to(char, {
        x: 0,
        y: 0,
        rotationX: 0,
        rotationY: 0,
        rotationZ: 0,
        duration: 0.5,
        ease: "power2.out",
      });
    });
  };

  return (
    <div style={{ textAlign: "center" }}>
      <div
        ref={containerRef}
        style={{
          display: "inline-block",
          perspective: 1000,
          userSelect: "none",
          fontSize: `${fontSize}rem`,
          fontWeight: 300,
          fontFamily: '"VT323", monospace',
        }}
      >
        {charArray.map((c, idx) => (
          <span
            key={idx}
            className="char"
            style={{
              display: "inline-block",
              position: "relative",
              marginRight: c.isSpace ? "0.5em" : "2px",
              transformStyle: "preserve-3d",
            }}
          >
            {c.char === " " ? "\u00A0" : c.char}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ScatterText;
